/**
 * Konstanten für den Cancel-Token-Codec.
 *
 * Single Source of Truth für Frontend, Backend und die externe link-erzeugende
 * Komponente (`PINK.appointmentFlow`). Layout-Vertrag siehe
 * `docs/cancel-token-wire-format.md`.
 */

/**
 * UNIX-Sekunden für 2026-01-01T00:00:00Z (UTC).
 * Basis für das `timeSlot`-Feld im Token (Sekunden seit dieser Epoche).
 */
export const TIMESLOT_EPOCH_UNIX = 1767225600 as const;

/**
 * Aktuelle Token-Layout-Version. Steht als erstes Byte im Token; jeder andere
 * Wert führt zu `VERSION_MISMATCH` im Decoder.
 */
export const TOKEN_VERSION = 0x01 as const;

/**
 * Bit-Limit für `appointmentId` (40 Bit).
 * Gültige Werte: 0 .. (APPOINTMENT_ID_BIT_LIMIT - 1n).
 */
export const APPOINTMENT_ID_BIT_LIMIT = 1n << 40n;

/**
 * Bit-Limit für `timeSlotSecondsFromEpoch` (40 Bit).
 * Reichweite: ~34_865 Jahre ab `TIMESLOT_EPOCH_UNIX`.
 */
export const TIMESLOT_BIT_LIMIT = 2 ** 40;

/**
 * Bit-Limit für `locationId` (16 Bit).
 */
export const LOCATION_BIT_LIMIT = 2 ** 16;

/**
 * Länge des `pinFingerprint`-Feldes in Bytes.
 * Identisch mit `SPKI_FINGERPRINT_BYTES` aus `../crypto-module/crypto.constants.ts` — hier
 * dupliziert, um die Token-Schicht crypto-frei zu halten. Tests verifizieren
 * die Gleichheit.
 */
export const PIN_FINGERPRINT_BYTE_LENGTH = 5 as const;

/**
 * Token-Länge in Bytes:
 * 1 (version) + 5 (appointmentId) + 5 (timeSlot) + 2 (location) + 5 (fingerprint)
 * = 18 Bytes.
 */
export const TOKEN_BYTE_LENGTH = 18 as const;

/**
 * Token-Länge in Base64URL-Zeichen ohne Padding.
 * 18 Bytes → 24 Base64URL-Zeichen exakt.
 */
export const TOKEN_BASE64URL_LENGTH = 24 as const;

/**
 * Toleranz für `timeSlot` in der Vergangenheit beim Plausibility-Check.
 * Tokens für Termine, die mehr als 1 Tag in der Vergangenheit liegen, werden
 * verworfen — schützt vor Manipulation der 24-Stunden-Sperre über extreme Werte.
 */
export const TOKEN_PLAUSIBILITY_PAST_MS = 24 * 60 * 60 * 1000;

/**
 * Toleranz für `timeSlot` in der Zukunft beim Plausibility-Check.
 * Tokens für Termine, die mehr als 365 Tage in der Zukunft liegen, werden
 * verworfen — Praxis-Buchungshorizonte sind deutlich kleiner.
 */
export const TOKEN_PLAUSIBILITY_FUTURE_MS = 365 * 24 * 60 * 60 * 1000;
